import React from 'react';

import { observer } from 'mobx-react';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';

import { DetailsRowContainer, TitleText } from 'components/Entities/AddEntityModal/styles';

import { useStore } from 'utils/hooks/store';

import API from 'utils/api';
import Logger from 'utils/logger';

import { EntityModules } from 'core/constants';

import { shortUnitName } from 'containers/OrganisationStructure/types';

import UnitPickerField from 'containers/OrganisationStructure/components/UnitPickerField';

import { UnitNote } from './styles';

export interface SiteUnitModalProps {
  siteUuid: string;
  siteName: string;
  currentUnitUuid: string | null;
  onChanged: (unitUuid: string) => void;
  onClose: () => void;
}

const SiteUnitModal: React.FunctionComponent<SiteUnitModalProps> = ({
  siteUuid,
  siteName,
  currentUnitUuid,
  onChanged,
  onClose,
}) => {
  const { notification, organisationStructure } = useStore();
  const [unitUuid, setUnitUuid] = React.useState<string | null>(currentUnitUuid);
  const [saving, setSaving] = React.useState(false);

  const units = organisationStructure.tree;

  const save = async (): Promise<void> => {
    if (!unitUuid) return;
    setSaving(true);
    try {
      await API.entities.setSiteUnit(siteUuid, unitUuid)();
      onChanged(unitUuid);
      notification.enqueueSuccessSnackbar(`${siteName} is now in ${shortUnitName(units, unitUuid)}`);
      onClose();
      return;
    } catch (e) {
      Logger.error('Could not set the unit on a site', e);
      notification.enqueueErrorSnackbar('Could not change the unit');
    }
    setSaving(false);
  };

  return (
    <CustomModal
      openModal
      onClose={onClose}
      modalTitle={`${currentUnitUuid ? 'Change unit' : 'Set unit'} for ${siteName}`}
      cancelButton={
        <Button onClick={onClose} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button
          onClick={save}
          disabled={!unitUuid || unitUuid === currentUnitUuid || saving}
          variant="contained"
          size="large"
        >
          {currentUnitUuid ? 'Change unit' : 'Set unit'}
        </Button>
      }
    >
      <DetailsRowContainer>
        <TitleText as="label" htmlFor="site-unit-change">
          Unit
        </TitleText>
        <UnitPickerField
          id="site-unit-change"
          units={units}
          value={unitUuid}
          onChange={setUnitUuid}
          placeholder="Select a unit"
          label="Unit"
        />
        {unitUuid && unitUuid !== currentUnitUuid ? (
          <UnitNote>This changes which Managers can see {siteName}.</UnitNote>
        ) : null}
      </DetailsRowContainer>
    </CustomModal>
  );
};

export default observer(SiteUnitModal);
